import { Play, Clock, Users, Star, Lock } from "lucide-react";
import { Link } from "react-router-dom";

interface CourseCardProps {
  id: string;
  title: string;
  instructor?: string;
  thumbnail: string;
  duration: string;
  students: number;
  rating: number;
  category: string;
  isLocked?: boolean;
}

const CourseCard = ({ id, title, instructor, thumbnail, duration, students, rating, category, isLocked = false }: CourseCardProps) => {
  return (
    <Link to={`/courses/${id}`} className="group block">
      <div className="glass-card rounded-xl overflow-hidden hover-lift press">
        {/* Thumbnail */}
        <div className="relative aspect-[16/10] overflow-hidden">
          <img 
            src={thumbnail} 
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/20 transition-all duration-300 flex items-center justify-center">
            <div className="w-10 h-10 rounded-full bg-primary-foreground/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {isLocked ? (
                <Lock className="w-4 h-4 text-primary" />
              ) : (
                <Play className="w-4 h-4 text-primary ml-0.5" fill="currentColor" />
              )}
            </div>
          </div>
          <div className="absolute top-3 left-3">
            <span className="px-2.5 py-1 rounded-md text-[10px] font-semibold bg-primary text-primary-foreground tracking-wide uppercase">
              {category}
            </span>
          </div>
          {isLocked && (
            <div className="absolute top-3 right-3">
              <span className="px-2 py-1 rounded-md text-[10px] font-semibold bg-foreground/70 text-primary-foreground flex items-center gap-1">
                <Lock className="w-2.5 h-2.5" />
                Locked
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          <h3 className="font-display text-sm font-semibold text-foreground mb-1 line-clamp-2 leading-snug group-hover:text-primary transition-colors">
            {title}
          </h3>
          {instructor && (
            <p className="text-xs text-muted-foreground mb-3">by {instructor}</p>
          )}

          {/* Meta */}
          <div className="flex items-center justify-between pt-3 border-t border-border">
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <span>{duration}</span>
              </div>
              <div className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                <span>{students.toLocaleString()}</span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Star className="w-3 h-3 text-accent" fill="currentColor" />
              <span className="text-xs font-semibold text-foreground">{rating.toFixed(1)}</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default CourseCard;
